import { AppFeature } from "~/server/models/appfeature"

// An optional ending for a note: buttons when there is somewhere useful to go
// (e.g. the docs), or an email address to write to. Notes that need nothing
// from the reader just end with "No action needed." in their text. Never
// "contact your account manager" by default.

export type ReleaseNoteAction = {
  note?: string
  links?: { label: string; href: string }[]
  // Shown as "Contact us at <email>." with the address written out, so it
  // can be copied when a mail app doesn't open.
  email?: string
}

// How loudly a log is announced beyond the Release Log itself. Leaving it out
// means "not announced", the default: most logs only live in the Release Log.
// "banner" is for changes to data people already rely on (restatements,
// methodology), anything that needs action, and breaking changes or removals.
// A banner log also gets an email to API users, who never see Console.
export type ReleaseNoteAnnouncement = {
  level: "banner"
  // One short, calm line naming the benefit, e.g. "Steadier US Foot Traffic
  // estimates". No dates, figures or alarming words ("restated",
  // "changed"): the specifics live in the log.
  message: string
  // The link to the log. Defaults to "See what changed".
  linkText?: string
  // Last day the bar shows, YYYY-MM-DD (inclusive).
  until: string
  // Only accounts holding this subscription resource see the bar. Leave it
  // out to show it to every logged-in account. The log itself is public
  // either way.
  audience?: AppFeature
}

export type ReleaseNote = {
  slug: string
  title: string
  summary: string
  // Publication date, YYYY-MM-DD.
  date: string
  // Tags from the unacast/release-notes repo vocabulary.
  products: string[]
  // Where it applies, as lowercase ISO 3166-1 alpha-3 codes ("usa", "nor").
  // Leave it out when it applies everywhere.
  geographies?: string[]
  // Which data it touches, as dataset families ("foot-traffic"). Leave it out
  // for changes that aren't about data (a product feature, the docs).
  datasets?: string[]
  // Markdown, headings start at `##`.
  body: string
  // Optional picture under the title, e.g. a product screenshot. Files live
  // in public/release-log/.
  image?: { src: string; srcSet?: string; alt: string }
  action?: ReleaseNoteAction
  announce?: ReleaseNoteAnnouncement
}

// First pass: a couple of notes written straight into the page, kept newest
// first by the sort below.
// The same shape as the unacast/release-notes repo, so entries can later be
// read from there instead.
export const RELEASE_NOTES: ReleaseNote[] = (
  [
    // From Jon's "US Foot Traffic Panel Fix, iteration 2" (#product-releases-
    // and-updates, 2026-10-16) and Sarah's roll-out script; copy approved by
    // Anna on 2026-10-19. Internal sources (Outlogic, FactSet) stay out.
    {
      slug: "us-foot-traffic-update",
      title: "Steadier US Foot Traffic estimates for Places",
      summary:
        "US Foot Traffic estimates for Places are now steadier and more reliable, especially for smaller locations and less-covered regions. History back to January 2019 has been restated. Areas follow.",
      date: "2026-10-19",
      products: ["insights", "console", "dataset-api", "data-delivery"],
      geographies: ["usa"],
      datasets: ["foot-traffic"],
      // Restated history: a change to data people already rely on. Places
      // only for now (product meeting, 2026-10-19); when Areas move to the
      // new panel, update the title, summary and body to cover both.
      announce: {
        level: "banner",
        message: "Steadier US Foot Traffic estimates",
        // The first banner, so it also introduces the Release Log itself.
        linkText: "Read it in the new Release Log",
        // Two weeks from 23 October (shown alongside the maintenance bar),
        // then we review how it went before deciding on more.
        until: "2026-11-06",
        // The restated data is the candidate version (PIN-147): only accounts
        // opted into it have it, so only they are told.
        audience: AppFeature.CANDIDATE_ACCESS,
      },
      body: `We've updated our US Foot Traffic data for Places. A substantially larger device panel now underpins the estimates across the full history from January 2019, giving steadier and more reliable visit estimates, particularly for smaller locations and less-covered regions.

This update covers Foot Traffic for Places. Foot Traffic for Areas follows, and we'll update this log when it does.

## What changes

To take full advantage of the update, history back to January 2019 has been restated. We normally avoid restating history, but the improvement is significant enough to justify an exception.

- **Visit levels** change little for most locations. Restated figures typically differ by less than 5% from those previously delivered.
- **Year-over-year trends** are more reliable. The larger panel gives a more stable baseline through 2025 and 2026, so comparisons spanning those years may differ from previously delivered figures.

## Aggregated at the source

The update is also part of our move to a privacy-safe way of operating, where device-level data never leaves the environment of the applications it comes from. We're working with our data providers to bring back data for Maryland, Virginia, Oregon and New Jersey on this basis, and will share a timeline once it's confirmed.

## If you use the API

The restated figures for Places are live in the Dataset API. If you store Places Foot Traffic data on your side, pull the history again from January 2019 to stay consistent.

No action needed in Insights or Console.`,
    },
    // DRAFT, to check with Sarah / Izabela before publishing: is Cross
    // Visits back in production (blocked 2026-07-02), does it work for any
    // brand, US only or Northern Europe too, is RetailNext live, and should a
    // paid add-on be in the public log at all. Built 2026-06-29 per
    // #team-insights-internal; never announced in #product-releases-and-updates.
    {
      slug: "brand-report",
      title:
        "Brand Report: benchmark a brand against its competitors in Insights",
      summary:
        "Find out where a brand stands against up to four competitors: foot traffic, location performance and audience side by side, so teams can spot changes early. An Insights add-on.",
      // Built 2026-06-29 (#team-insights-internal); dated 2026-08-31 per
      // Anna.
      date: "2026-08-31",
      products: ["insights"],
      // US only (confirmed by Anna). It needs store-level visits per brand,
      // which only the US data has: SafeGraph tags each US place with its
      // brand. Nordic data is building-level, where one brand's store
      // performance is out of scope (Frode, #proj-precise-visitation-nordics).
      geographies: ["usa"],
      // Figma "Landing Page - Internal Assets", node 853:3548.
      image: {
        src: "/release-log/brand-report.jpg",
        srcSet:
          "/release-log/brand-report-800.jpg 800w, /release-log/brand-report.jpg 1600w",
        alt: "The Brand Report in Insights: a visitor profile radar chart comparing two brands, beside a peer comparison page with year-over-year foot traffic",
      },
      body: `Find out where your brand stands against its competitors before they find out first. The Brand Report compares foot traffic, location performance and audience with up to four peers, so strategy, marketing and real estate teams can spot changes early and act on them.

## What's in it

**Year-over-year foot traffic**

- **Brand and per location:** how total traffic changed for each brand's whole fleet, and for its typical location.
- **Fleet status:** how many locations each brand had in each year, and how many of them grew, held flat or declined.
- **Monthly variation:** each month against the year before, with competitors as a benchmark.

**Audience evolution**

- **Demographics:** how visitors’ age, income and education are changing.
- **Psychographic profiles:** which lifestyle segments, from urban to rural, each brand attracts.

## How it works

- Choose a brand, its competitors and a period by quarter.
- Look at the whole US or narrow it to a state or metro area.
- Save in the app or export as a PDF.`,
      // Add-on requests go to the support inbox, not account managers.
      action: {
        note: "The Brand Report is an add-on for Insights accounts.",
        email: "support@pine59.com",
      },
    },
    {
      slug: "public-documentation",
      title: "Pine59 documentation is now public",
      summary:
        "Everything you need to understand and use Pine59 data is now open to everyone at console.pine59.com/docs, including your AI agent.",
      date: "2026-09-01",
      products: ["docs", "dataset-api"],
      // The docs launch share image (Console's og-image.png at launch): the
      // Guides & examples page on the forest topo backdrop.
      image: {
        src: "/release-log/docs-launch.jpg",
        srcSet:
          "/release-log/docs-launch-800.jpg 800w, /release-log/docs-launch.jpg 1200w",
        alt: 'Pine59 documentation: "Measure. Understand. Build." beside the Guides & examples page, on a dark green map texture',
      },
      body: `Everything you need to understand and use Pine59 data is now at [console.pine59.com/docs](https://console.pine59.com/docs). It's open to everyone, including your AI agent, and you don't need to log in.

## What's in it

- **Every dataset, per country.** Fields, cadence, coverage and how each one is produced.
- **Concepts.** What foot traffic, trade areas and visitor journeys measure, and how to read them.
- **Guides and worked examples.** Step-by-step recipes for common questions, using real data.
- **The full Dataset API reference.** Every endpoint, with examples in curl and Python, served from \`api.pine59.com\`.

You can now find the right dataset, share a page with a colleague, or plan an integration before you request access.

## Made for AI assistants too

More and more of you use ChatGPT, Claude or your own agents to work with data, so the docs are built to be read by them as well:

- Every page has a plain Markdown version. [\`llms.txt\`](https://console.pine59.com/llms.txt) points AI tools to all of them, so an assistant can learn our datasets and API from the source.
- The Dataset API is described in a machine-readable spec at [\`https://api.pine59.com/openapi.json\`](https://api.pine59.com/openapi.json). You can load it into a Custom GPT, an agent framework or a code generator.
- The [Build with AI](https://console.pine59.com/docs/guides/build-with-ai) guide shows how to connect your assistant to your Pine59 data, with a ready-made system prompt.

## This is just the start

We're constantly improving the documentation and adding to it, with new datasets, guides and worked examples on the way. Is something missing, or is a page unclear? Tell us with the feedback at the bottom of every docs page. It shapes what we write next.`,
      action: {
        links: [{ label: "Browse the docs", href: "/docs" }],
      },
    },
  ] satisfies ReleaseNote[]
).sort((a, b) => b.date.localeCompare(a.date))

export function getReleaseNote(
  slug: string | undefined,
): ReleaseNote | undefined {
  return RELEASE_NOTES.find((n) => n.slug === slug)
}
